import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../services/apiServices';
import { useFeedback } from '../../contexts/FeedbackContext';
import { ArrowLeftIcon, UserIcon } from '../../components/common/Icons';
import { AuthField } from './LoginPage';
import { requiredFieldMessage, requiredFieldsMessage } from '../../utils/validationMessages';

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { notify } = useFeedback();
  const { t } = useTranslation();
  const token = params.get('token') || '';
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = {};
    const missingLabels = [];
    if (!form.password) {
      errors.password = requiredFieldMessage(t, t('auth.newPassword'));
      missingLabels.push(t('auth.newPassword'));
    }
    if (!form.confirm) {
      errors.confirm = requiredFieldMessage(t, t('auth.confirmPassword'));
      missingLabels.push(t('auth.confirmPassword'));
    }
    if (missingLabels.length) {
      setFieldErrors(errors);
      notify({ variant: 'warning', title: t('feedback.passwordInvalidTitle'), message: requiredFieldsMessage(t, missingLabels) });
      return;
    }
    if (form.password.length < 8) {
      setFieldErrors({ password: t('feedback.passwordInvalidMessage') });
      notify({ variant: 'warning', title: t('feedback.passwordInvalidTitle'), message: t('feedback.passwordInvalidMessage') });
      return;
    }
    if (form.password !== form.confirm) {
      setFieldErrors({ confirm: t('feedback.passwordMismatchMessage') });
      notify({ variant: 'warning', title: t('feedback.passwordMismatchTitle'), message: t('feedback.passwordMismatchMessage') });
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      await authAPI.resetPassword(token, form.password);
      await notify({ variant: 'success', title: t('feedback.passwordResetTitle'), message: t('feedback.passwordResetMessage') });
      navigate('/login');
    } catch (err) {
      notify({ variant: 'danger', title: t('feedback.tokenInvalidTitle'), message: err.response?.data?.error || t('feedback.tokenInvalidMessage') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-soft">
      <h1 className="text-center text-2xl font-bold text-slate-900">{t('auth.resetTitle')}</h1>
      <p className="mt-3 text-center text-slate-600">{t('auth.resetSubtitle')}</p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
        <AuthField
          label={t('auth.newPassword')}
          type="password"
          value={form.password}
          onChange={(value) => {
            setForm({ ...form, password: value });
            setFieldErrors((current) => ({ ...current, password: '' }));
          }}
          placeholder="••••••••"
          icon={<UserIcon className="h-5 w-5" />}
          autoComplete="new-password"
          revealable
          error={fieldErrors.password}
        />
        <AuthField
          label={t('auth.confirmPassword')}
          type="password"
          value={form.confirm}
          onChange={(value) => {
            setForm({ ...form, confirm: value });
            setFieldErrors((current) => ({ ...current, confirm: '' }));
          }}
          placeholder="••••••••"
          icon={<UserIcon className="h-5 w-5" />}
          autoComplete="new-password"
          revealable
          error={fieldErrors.confirm}
        />
        <button
          className="inline-flex h-12 w-full items-center justify-center rounded-lg bg-sky-500 px-5 font-bold text-white shadow-soft transition hover:bg-sky-600 disabled:opacity-60"
          type="submit"
          disabled={loading}
        >
          {loading ? t('common.processing') : t('auth.resetButton')}
        </button>
      </form>

      <Link to="/login" className="mt-7 flex items-center justify-center gap-2 font-bold text-sky-500 hover:text-sky-600">
        <ArrowLeftIcon className="h-4 w-4" />
        {t('auth.backToLogin')}
      </Link>
    </div>
  );
};

export default ResetPasswordPage;
